import { memo, useCallback, useEffect, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  CircularProgress
} from '@mui/material';
import { cloneDeep } from 'lodash';

const FormWrapper = memo(({
  children,
  footer,
  onSubmit,
  validationSchema,
  defaultValues = {},
  mode = 'all',
  resetOnSubmit = false,
  loading = false,
  formProps = {},
  containerSx = {},
  ...hookFormProps
}) => {
  const formMethods = useForm({
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    defaultValues,
    mode,
     shouldUnregister: false, 
    ...hookFormProps
  });
  const {
    handleSubmit,
    formState: { isSubmitting },
    reset,
    clearErrors,
  } = formMethods;

  const didInitialize = useRef(false);

  // NOTE: The below useEffect should run only once to assign default value to the form
  useEffect(() => {
    if (
      !didInitialize.current &&
      defaultValues &&
      Object.keys(defaultValues).length > 0
    ) {
      reset(cloneDeep(defaultValues), {
        keepDirtyValues: true,
        // keepDefaultValues: true, //@info keeping it might required.
        // keepValues: true,
        keepDirty: true,
      });
      didInitialize.current = true;
    }
  }, [defaultValues, reset]);

  const handleFormSubmit = useCallback(async (data) => {
    try {
      clearErrors();
      await onSubmit(data);
      if (resetOnSubmit) {
        reset(defaultValues);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      throw error;
    }
  }, [onSubmit, resetOnSubmit, reset, defaultValues, clearErrors]);

 const isFormLoading = loading || isSubmitting;

  return (
    <FormProvider {...formMethods}>
      <Box sx={{ width: '100%', ...containerSx }}>
        <Box
          component="form"
          onSubmit={handleSubmit(handleFormSubmit)}
          {...formProps}
        >
          {isFormLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                borderRadius: 1
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {children}
          {footer}
        </Box>
      </Box>
    </FormProvider>
  );
});

FormWrapper.displayName = 'FormWrapper';

export default FormWrapper;